import { useEffect, useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Button, Col, Row } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { WorkerMiniProfile } from "../../../components/WorkerMiniProfile/WorkerMiniProfile";
import { useUserDetails } from "../../../api/users";

/**
 * Admin (and sub-admin) view of a worker mini-profile.
 * Uses GET admin/user/details/:slug — same payload as full worker details.
 * Optional `location.state.user` skips the fetch (for client flows that already have the payload).
 */
const WorkerMiniProfilePage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const presetUser = location.state?.user;
  const userDetailsMutation = useUserDetails();

  useEffect(() => {
    if (presetUser || !slug) return;
    userDetailsMutation.mutate(slug);
  }, [slug, presetUser]);

  const payload = userDetailsMutation.data?.data ?? userDetailsMutation.data ?? {};
  const user = useMemo(() => {
    if (presetUser && typeof presetUser === "object") return presetUser;
    return payload && typeof payload === "object" ? payload : {};
  }, [presetUser, payload]);

  document.title = "Worker mini profile | Admin";

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs title="Users" breadcrumbItem="Worker mini profile" />

        <Row className="mb-3 align-items-center">
          <Col>
            <Button tag={Link} to="/user/list" color="light" outline className="mb-2 text-primary">
              <i className="mdi mdi-arrow-left me-1 text-primary" />
              Back to users
            </Button>
            {slug && (
              <Button
                tag={Link}
                to={`/user/user-details/worker/${slug}`}
                color="primary"
                outline
                className="mb-2 ms-2"
              >
                <i className="mdi mdi-account-details-outline me-1" />
                Full profile
              </Button>
            )}
          </Col>
        </Row>

        {!presetUser && userDetailsMutation.isPending && (
          <div className="text-muted py-5 text-center">Loading profile…</div>
        )}
        {!presetUser && userDetailsMutation.error && (
          <div className="text-danger py-3">Failed to load worker profile.</div>
        )}
        {(presetUser || (!userDetailsMutation.isPending && !userDetailsMutation.error)) && (
          <WorkerMiniProfile user={user} />
        )}
      </div>
    </div>
  );
};

export default WorkerMiniProfilePage;
